import React, { useEffect, useRef } from 'react';
import Map, { Marker, MapRef } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { LatLng } from '../../entities/geo/types';
import { MAPBOX_TOKEN, MissingTokenPlaceholder, Pin } from './mapPrimitives';

/**
 * PointsMap — a scatter of independent labeled markers on a map.
 *
 * Use when the points have no relationship to each other (e.g. friends on a
 * map). For connected points (origin → destination, ordered stops), use
 * JourneyMap or a future route-specific component instead.
 *
 * The `overlay` prop renders centered on top of the map — useful for
 * empty-state messages.
 */

export interface MapMarker {
  id: string | number;
  lat: number;
  lng: number;
  label: string;
  color: string;
}

interface PointsMapProps {
  markers: MapMarker[];
  height?: number;
  overlay?: React.ReactNode;
  /** Initial center used when there are no markers to fit. */
  initialCenter?: LatLng;
  /** Initial zoom used when there are no markers to fit. */
  initialZoom?: number;
}

const DEFAULT_CENTER: LatLng = { lat: 50, lng: 10 }; // Europe-ish
const DEFAULT_ZOOM = 3;

const PointsMap: React.FC<PointsMapProps> = ({
  markers,
  height = 320,
  overlay,
  initialCenter,
  initialZoom,
}) => {
  const mapRef = useRef<MapRef | null>(null);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const finite = markers.filter((m) => isFinite(m.lat) && isFinite(m.lng));
    if (finite.length === 0) return;

    if (finite.length === 1) {
      map.flyTo({ center: [finite[0].lng, finite[0].lat], zoom: 6, duration: 600 });
      return;
    }

    let minLng = finite[0].lng, maxLng = finite[0].lng;
    let minLat = finite[0].lat, maxLat = finite[0].lat;
    for (const m of finite) {
      if (m.lng < minLng) minLng = m.lng;
      if (m.lng > maxLng) maxLng = m.lng;
      if (m.lat < minLat) minLat = m.lat;
      if (m.lat > maxLat) maxLat = m.lat;
    }

    map.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: 60, duration: 600, maxZoom: 9 }
    );
  }, [markers]);

  if (!MAPBOX_TOKEN) return <MissingTokenPlaceholder height={height} />;

  const center = initialCenter ?? DEFAULT_CENTER;
  const zoom = initialZoom ?? DEFAULT_ZOOM;

  return (
    <div
      style={{
        position: 'relative',
        height,
        borderRadius: 8,
        overflow: 'hidden',
        border: '1px solid #e2e8f0',
      }}
    >
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{ longitude: center.lng, latitude: center.lat, zoom }}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        style={{ width: '100%', height: '100%' }}
      >
        {markers.map((m) => (
          <Marker key={m.id} longitude={m.lng} latitude={m.lat} anchor="bottom">
            <Pin color={m.color} label={m.label} />
          </Marker>
        ))}
      </Map>

      {overlay && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          {overlay}
        </div>
      )}
    </div>
  );
};

export default PointsMap;
